import { H2, H6, H7, Small } from "@/components/standard/BFText"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetFlatList,
  BottomSheetScrollView,
  BottomSheetModal,
  BottomSheetView,
  useBottomSheet,
} from "@gorhom/bottom-sheet"
import React, {
  useCallback,
  useRef,
  useContext,
  type ReactNode,
  type RefObject,
  createContext,
  Suspense,
} from "react"
import { BFColors } from "@/constants/Colors"
import { $isLoanListModalOpen } from "@/stores/loanDashboard"
import { debounce } from "lodash"
import { Platform, StyleSheet, View } from "react-native"
import { Image } from "expo-image"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import XIcon from "phosphor-react-native/src/icons/X"
import RightChevron from "@/assets/icons/right-chevron-blue.svg"
import { BFPressable } from "@/components/buttons/CommonButtons"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/tabs/DashboardModalTabs"
import { useFocusEffect } from "@react-navigation/native"
import { cn } from "@/lib/utils"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import {
  loanDashboardListInfiniteQueryOptions,
  useUnArchiveApplicationMutation,
  type LoanListQueryItem,
} from "@/queries/loan-dashboard"
import { type LoanDashboardStatus, type LoanStatus } from "@/generated/graphql"
import { router } from "expo-router"
import { loanAppDashboardPath } from "@/screens/loans/utils/LoanUtils"
import { showSuccessToast } from "@/components/toasts/AppToast"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { toTitleCase } from "@/utils/StringUtils"

type LoanDashboardLoanListModalRefType = BottomSheetModal

const LoanDashboardLoanListModalContext =
  createContext<RefObject<LoanDashboardLoanListModalRefType> | null>(null)

interface LoanDashboardLoanListModalProviderProps {
  children: ReactNode
}

export const LoanDashboardLoanListModalProvider: React.FC<
  LoanDashboardLoanListModalProviderProps
> = ({ children }) => {
  const ref = useRef<LoanDashboardLoanListModalRefType>(null)
  return (
    <LoanDashboardLoanListModalContext.Provider value={ref}>
      {children}
    </LoanDashboardLoanListModalContext.Provider>
  )
}

export const useLoanDashboardLoanListModalRef = () => {
  const ref = useContext(LoanDashboardLoanListModalContext)
  if (!ref)
    throw new Error(
      "useLoanDashboardLoanListModalRef must be used within a useLoanDashboardLoanListModalRefProvider"
    )
  return ref
}

export function useLoanDashboardLoanListModal() {
  const bottomSheetModalRef = useLoanDashboardLoanListModalRef()
  const onShow = useCallback(
    debounce(
      () => {
        bottomSheetModalRef.current?.present()
        $isLoanListModalOpen.set(true)
        return () => {
          $isLoanListModalOpen.set(false)
        }
      },
      100,
      { leading: true }
    ),
    []
  )
  return { onShow }
}

const backDropStyleOverride = StyleSheet.create({
  background: {
    backgroundColor: BFColors.midnightBlue,
  },
})

export function LoanDashboardLoanListModal() {
  const { isWeb } = useDeviceAttributes()
  const bottomSheetModalRef = useLoanDashboardLoanListModalRef()
  const renderModalBackdrop = useCallback((props: BottomSheetBackdropProps) => {
    const { style: defaultStyle, ...rest } = props

    return (
      <BottomSheetBackdrop
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        style={[defaultStyle, backDropStyleOverride.background]}
        opacity={0.95}
        {...rest}
      />
    )
  }, [])
  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={renderModalBackdrop}
      index={0}
      backgroundStyle={{ backgroundColor: BFColors.offWhite, flex: 1 }}
      handleIndicatorStyle={{ backgroundColor: BFColors.offWhite }}
      handleStyle={{
        padding: 0,
      }}
      overDragResistanceFactor={0}
      enableDynamicSizing={false}
      enableOverDrag={false}
      snapPoints={isWeb ? ["75%"] : ["60%", "80%"]}
      containerStyle={{
        flex: 1,
      }}
      style={{
        padding: 0,
        margin: 0,
        borderTopRightRadius: 20,
        borderTopLeftRadius: 20,
        flex: 1,
        maxWidth: 800,
        marginHorizontal: "auto",
      }}
      onDismiss={() => {
        $isLoanListModalOpen.set(false)
      }}
    >
      <BottomSheetView className="flex flex-col justify-start flex-1 w-full">
        <Vertical className="items-start justify-start gap-5 px-5 pt-3">
          <Horizontal className="items-start justify-end w-full py-3.5">
            <BFPressable
              testID="close-loan-list-modal"
              onPress={() => {
                bottomSheetModalRef.current?.dismiss()
              }}
              title="Loan Dashboard Loan List Modal Close"
            >
              {(pressed) => {
                return (
                  <XIcon
                    color={BFColors.midnightBlue}
                    style={{
                      opacity: pressed ? 0.5 : 1,
                    }}
                  />
                )
              }}
            </BFPressable>
          </Horizontal>
          <Vertical className="items-center justify-start w-full h-12">
            <H2>Loan Properties</H2>
          </Vertical>
        </Vertical>
        <LoanListModalBody />
      </BottomSheetView>
    </BottomSheetModal>
  )
}

function LoanListItem({ item }: { item: LoanListQueryItem }) {
  const unArchiveMutation = useUnArchiveApplicationMutation()
  const { close } = useBottomSheet()

  const closeModalWithDelay = useCallback(
    debounce(() => {
      close()
    }, 200),
    []
  )

  const navigateAndClose = (applicationId: string) => {
    router.dismissTo(loanAppDashboardPath(applicationId))
    closeModalWithDelay()
  }

  const streetAddress = item.property.street
  const cityStateZip =
    `${item.property.city || ""}, ${item.property.state || ""} ${item.property.zipcode}`.trim()

  const showUnArchiveButton = item.status === "archived"

  return (
    <BFPressable
      title="Loan Dashboard List Item"
      testID={"loan-dashboard-list-item"}
      className="flex flex-col w-full h-[94px] "
      disabled={showUnArchiveButton}
      onPress={() => {
        navigateAndClose(item.id)
      }}
    >
      {({ pressed }) => {
        return (
          <Vertical
            className={cn(
              "justify-center items-start self-stretch py-[15px] px-5 w-full h-[94px] border-b border-light-grey flex-1",
              pressed && "opacity-50"
            )}
          >
            <Horizontal className="items-center justify-start w-full">
              <Image
                source={item.property.heroImage}
                style={{
                  flexDirection: "column",
                  flexShrink: 0,
                  gap: 10,
                  justifyContent: "flex-end",
                  alignItems: "center",
                  width: 70,
                  height: 64,
                  borderRadius: 4,
                  borderColor: BFColors.lightGrey,
                  borderWidth: 1,
                }}
              />
              <Vertical className="items-start justify-center px-2 shrink-0">
                <ApplicationListStatusPill status={item.status} />
                <H6>{streetAddress}</H6>
                <Small>{cityStateZip}</Small>
              </Vertical>
              <Horizontal
                className={cn(
                  "items-center justify-end pl-2.5 h-[64px] shrink-0 grow",
                  showUnArchiveButton ? "visible" : "invisible"
                )}
              >
                <BFPressable
                  testID="dashboard-loan-archive-button"
                  title="Loan Dashboard Archive"
                  className="flex flex-row items-center justify-center"
                  loading={unArchiveMutation.isPending}
                  disabled={!showUnArchiveButton}
                  onPress={() => {
                    unArchiveMutation.mutate(
                      { loanId: item.id },
                      {
                        onSuccess(resp) {
                          showSuccessToast({
                            title: `Restored ${streetAddress}`,
                            message: "You're loan inquiry has been restored.",
                            autoHide: true,
                            visibilityTime: 5000,
                          })
                          const redirectId =
                            resp.unArchiveApplication?.application?.id
                          if (redirectId) {
                            navigateAndClose(redirectId)
                          } else {
                            router.replace("/loans")
                          }
                        },
                      }
                    )
                  }}
                >
                  {({ pressed, loading }) => {
                    return (
                      <Horizontal
                        className={cn(
                          "justify-center items-center self-stretch gap-[3px]",
                          (pressed || loading) && "opacity-50"
                        )}
                      >
                        <Small className="underline text-calm-blue">
                          Unarchive
                        </Small>
                      </Horizontal>
                    )
                  }}
                </BFPressable>
              </Horizontal>
              <Horizontal
                className={cn(
                  "items-center justify-start p-[5px]",
                  showUnArchiveButton ? "hidden" : "visible"
                )}
              >
                <RightChevron height={18} width={26} />
              </Horizontal>
            </Horizontal>
          </Vertical>
        )
      }}
    </BFPressable>
  )
}

const LoanItemList = ({ status }: { status: LoanDashboardStatus }) => {
  const isWeb = Platform.OS === "web"

  const { data } = useSuspenseInfiniteQuery(
    loanDashboardListInfiniteQueryOptions(status)
  )

  const contentContainerClassName = cn(
    "flex flex-col justify-start items-start self-stretch w-full min-h-full",
    "web:max-h-full web:overflow-y-auto"
  )

  if (isWeb) {
    return (
      <BottomSheetScrollView
        contentContainerClassName={contentContainerClassName}
      >
        {data.map((item) => {
          if (!item) return null
          return <LoanListItem key={item.id} item={item} />
        })}
      </BottomSheetScrollView>
    )
  }

  return (
    <BottomSheetFlatList
      nestedScrollEnabled={true}
      data={data}
      renderItem={(itemToRender) => {
        if (!itemToRender?.item) return null
        return (
          <LoanListItem key={itemToRender.item.id} item={itemToRender.item} />
        )
      }}
      className={"flex flex-col flex-1"}
      focusHook={useFocusEffect}
      contentContainerClassName={contentContainerClassName}
    />
  )
}

function LoanListModalBody() {
  const [value, setValue] = React.useState("quote")
  return (
    <Vertical className="items-center justify-start flex-1 bg-off-white pt-2.5">
      <Tabs
        value={value}
        onValueChange={setValue}
        className="flex flex-col w-full py-2.5 gap-2.5 flex-1"
      >
        <TabsList className="flex flex-row items-center self-stretch w-full h-6">
          <TabsTrigger
            value="quote"
            title={"Dashboard Quote Tab"}
            testID={"dashboard-tab-quote"}
          >
            {() => <H6>Quote</H6>}
          </TabsTrigger>
          <TabsTrigger
            value="active"
            title={"Dashboard Active Tab"}
            testID={"dashboard-tab-active"}
          >
            {() => {
              return <H6>Active</H6>
            }}
          </TabsTrigger>
          <TabsTrigger
            value="repaid"
            title={"Dashboard Repaid Tab"}
            testID={"dashboard-tab-repaid"}
          >
            {() => {
              return <H6>Repaid</H6>
            }}
          </TabsTrigger>
          <TabsTrigger
            value="archived"
            title={"Dashboard Archived Tab"}
            testID={"dashboard-tab-archive"}
          >
            {() => {
              return <H6>Archived</H6>
            }}
          </TabsTrigger>
        </TabsList>
        <Horizontal className="flex-1 pt-2.5">
          <StatusTabContent status={"quote"} />
          <StatusTabContent status={"active"} />
          <StatusTabContent status={"repaid"} />
          <StatusTabContent status={"archived"} />
        </Horizontal>
      </Tabs>
    </Vertical>
  )
}

function StatusTabContent({ status }: { status: LoanDashboardStatus }) {
  return (
    <TabsContent className="flex flex-row flex-1" value={status}>
      <Suspense fallback={null}>
        <LoanItemList status={status} />
      </Suspense>
    </TabsContent>
  )
}

interface ApplicationListStatusPillProps {
  status: LoanStatus
}

function ApplicationListStatusPill({ status }: ApplicationListStatusPillProps) {
  const applicationIsIncomplete = status === "application_started"
  // Just in case this changes again
  const textColor = applicationIsIncomplete
    ? "text-midnight-blue"
    : "text-midnight-blue"
  const bgColor = applicationIsIncomplete ? "bg-salmon" : "bg-beige"

  return (
    <View testID="loan-item-status">
      <Horizontal
        className={cn(
          "mb-1.5 items-center justify-center p-1.5 h-[15px]",
          bgColor
        )}
      >
        <H7 className={cn("leading-[16px] uppercase h-[16px]", textColor)}>
          {toTitleCase(status)}
        </H7>
      </Horizontal>
    </View>
  )
}
